import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { N0, N700 } from '@atlaskit/theme/colors';
import WorldIcon from '@atlaskit/icon/glyph/world';
import OfficeBuildingIcon from '@atlaskit/icon/glyph/office-building';
import { Flex, Text } from '@atlaskit/primitives';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { Appearance } from '@atlaskit/button';
import { getParentContainerId } from '@atlassian/cssxp-utils-entitlement';
import { RelationshipPathType } from '@atlassian/cssxp-typescommon';
import { EntitlementIcon, useFeatureToggle, useModalDialog, CopyableText } from '@css-xp/base';
import { EntitlementStatus } from '@css-xp/base/entitlement-utils';
import { useFetchEccFromContactService } from '@css-xp/contact-resources';
import { isTrialEntitlement } from '@css-xp/entitlements-v2';
import { CloudSiteLink, OrgNameLink } from '@css-xp/ppc-common';
import {
  EntitlementDetails as EntitlementDetailsType,
  FetchLinkedEntitlementsPpcQuery,
  FetchLinkedEntitlementsPpcQueryVariables,
  LinkedEntitlementTypes,
  FetchInvoiceGroupDetailsPpcQuery,
  FetchInvoiceGroupDetailsPpcQueryVariables,
  FetchSandboxParentPpcQuery,
  FetchSandboxParentPpcQueryVariables,
  BillingStatus,
  ContactInfo,
  ContactType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import { useGetRelatedDomainsForCurrentTxA, useTitle } from '@css-xp/ppc-custom-hooks';
import { PPC_PAGE_TITLE, ENABLE_DESIGN_ITERATION_1, routePaths, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { ADVOCATE_RESOURCE_TYPE, useAdvocateAccessControl, ADVOCATE_ACCESS_TYPE } from '@css-xp/ppc-access-control';
import { EntitlementDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import {
  isBilledElsewhereProduct,
  isRovoInstanceProduct,
  isRovoOrgProduct,
  isFutureDatedInactiveSubscription,
  getIsBillingProfileUsedForInternalUsageLicense,
  isOrgProduct,
} from '@css-xp/ppc-utils';
import { BilledExternallyTag } from '@css-xp/ppc-billed-externally';
// eslint-disable-next-line workspaces/require-dependency
import { InProductHelpFeedback } from '@css-xp/ppc-in-product-help';
import { fetchLinkedEntitlementsPPC } from '../../../queries/fetch-linked-entitlements-ppc';
import { fetchInvoiceGroupDetailsPPCQuery } from '../../../queries/fetch-invoice-group-details-ppc';
import { fetchSandboxParentPPC } from '../../../queries/fetch-sandbox-parent-ppc';
import { EntitlementHeaderActions } from '../entitlement-header/entitlement-header-actions';
import { EntitlementPageTabs } from '../entitlement-page-tabs/entitlement-page-tabs';
import { EntitlementSummary } from '../entitlement-summary/entitlement-summary';
import { ChangeSubscriptionWarningModal } from './change-subscription-warning-modal';
import { EntitlementBanner } from '../entitlement-header/entitlement-banner';
import './entitlement-details.scss';
import { EntitlementDetailsHeader } from './entitlement-details-heading.styles';
import { EntitlementCards } from '../entitlement-cards/entitlement-cards';

interface EntitlementDetailsProps {
  entitlement: EntitlementDetailsType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId?: string | null;
}

export const EntitlementDetails = ({ entitlement, refetch, transactionAccountId }: EntitlementDetailsProps) => {
  const [eccDetails, setEccDetails] = useState<ContactInfo[]>([]);

  const { isModalOpen, closeModalDialog } = useModalDialog();
  const { entitlementId } = useParams<{ entitlementId: string }>();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const openFeedbackWindow = () => {
    setIsFeedbackOpen(true);
  };
  const onCloseFeedbackWindow = () => {
    setIsFeedbackOpen(false);
  };
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isAddOnsTabEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isSandboxDecouplingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING);
  const isFutureDatedEntitlementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS);
  const isInternalUsageLicensesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_INTERNAL_USAGE_LICENSES_SUPPORT);
  const isGracePeriodVisibilityEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_GRACE_PERIOD_VISIBILITY);
  const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);
  const isMultiInstanceAppsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);
  const isAppEditionSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT);

  const { isApp, id, productType, isSandbox, activeSubscriptionBillingGroupId, billingGroupId } = entitlement;
  useTitle(entitlement.entitlementNumber ?? '', PPC_PAGE_TITLE.SUBSCRIPTIONS);
  const isNewTab = !window.history.state;

  const canAdvocatesManageSubscription = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.UPDATE,
    resource: ADVOCATE_RESOURCE_TYPE.SUBSCRIPTION,
  });

  const { getECCDetails } = useFetchEccFromContactService(ContactType.CUSTOMER_CONTACT);

  const { relatedDomains } = useGetRelatedDomainsForCurrentTxA();

  const fetchEccDetails = useCallback(async () => {
    const invoiceGroupId = activeSubscriptionBillingGroupId ?? billingGroupId;
    if (isInternalUsageLicensesEnabled && transactionAccountId && invoiceGroupId) {
      const data = await getECCDetails(invoiceGroupId, transactionAccountId);
      setEccDetails(data);
    }
  }, [
    activeSubscriptionBillingGroupId,
    billingGroupId,
    getECCDetails,
    isInternalUsageLicensesEnabled,
    transactionAccountId,
  ]);

  useEffect(() => {
    fetchEccDetails();
  }, [fetchEccDetails]);

  const { data, loading } = useQuery<FetchLinkedEntitlementsPpcQuery, FetchLinkedEntitlementsPpcQueryVariables>(
    fetchLinkedEntitlementsPPC,
    {
      variables: {
        id: entitlementId,
        searchLinkedEntitlementParams: {
          entitlementId: id,
          containerId: getParentContainerId(entitlement) ?? null,
          currentEntitlementType: isSandbox
            ? LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT
            : LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
          destinationEntitlementType: isSandbox
            ? [LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]
            : [LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT],
          productType: productType,
          findProducts: true,
          findApps: false,
        },
        transactionAccountId,
      },
      displayName: 'fetchAppEntitlements',
      notifyOnNetworkStatusChange: true,
      skip: !isApp,
    }
  );

  const invoiceGroupDetails = useQuery<FetchInvoiceGroupDetailsPpcQuery, FetchInvoiceGroupDetailsPpcQueryVariables>(
    fetchInvoiceGroupDetailsPPCQuery,
    {
      // @ts-ignore
      variables: { id: activeSubscriptionBillingGroupId ?? billingGroupId ?? '', txaId: transactionAccountId },
    }
  );

  const isBillingProfileUsedForInternalUsageLicense = useMemo(() => {
    if (isInternalUsageLicensesEnabled && !invoiceGroupDetails.loading) {
      return getIsBillingProfileUsedForInternalUsageLicense({
        eccDetails,
        shipToPartyId: invoiceGroupDetails?.data?.invoiceGroup?.shipToParty?.id,
        relatedDomains: relatedDomains,
      });
    }
    return false;
  }, [isInternalUsageLicensesEnabled, eccDetails, invoiceGroupDetails, relatedDomains]);

  const parentProductEntitlements = useMemo(() => {
    return data?.entitlementDetails?.linkedEntitlements ?? [];
  }, [data]);

  const sandboxParentEntitlementId = entitlement?.relatesFromEntitlements?.find(
    (relationShipObj) => relationShipObj?.relationshipType === RelationshipPathType.SANDBOX_DEPENDENCE
  )?.entitlementId;

  const { data: sandboxParent, loading: sandboxParentLoading } = useQuery<
    FetchSandboxParentPpcQuery,
    FetchSandboxParentPpcQueryVariables
  >(fetchSandboxParentPPC, {
    variables: { id: sandboxParentEntitlementId, transactionAccountId, fetchDetails: isDesignIteration1Enabled },
    displayName: 'fetchSandboxParentPPC',
    notifyOnNetworkStatusChange: true,
    skip: !isSandboxSupportForSubscriptionsEnabled && !entitlement?.isSandbox,
  });

  if (loading) {
    if (isDesignIteration1Enabled) {
      return <EntitlementDetailsPageLoader />;
    }

    return <LoadingIndicator />;
  }

  const isFreePlan = entitlement.edition ? entitlement.edition.includes('Free') : false;
  const isStatusPage = entitlement?.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isTrial = !isFreePlan && isTrialEntitlement(entitlement, isStatusPage);
  const isCloud = entitlement?.platform?.trim().toUpperCase() === 'CLOUD';
  const isBilledElsewhere = isBilledElsewhereProduct(entitlement?.productId);
  const isExternallyBilled = entitlement?.isExternallyBilled;

  const showPageActions = canAdvocatesManageSubscription && !isRovoInstanceProduct(entitlement);

  const isFutureDatedInActiveEntitlement =
    isFutureDatedEntitlementEnabled &&
    isFutureDatedInactiveSubscription(
      !!entitlement?.isFutureDatedSubscription,
      entitlement?.billingStatus as BillingStatus
    );

  const renderEntitlementIcon = () => {
    return (
      <EntitlementIcon
        logoId={entitlement?.logoId as string}
        productId={entitlement?.productId as string}
        className="header-icon"
        isCloud={isCloud}
      />
    );
  };

  const renderEntitlementStatus = () => (
    <EntitlementStatus
      billingStatus={entitlement?.billingStatus}
      provisioningStatus={entitlement?.provisioningStatus}
      scheduledChanges={entitlement?.scheduledChanges}
      entitlement={entitlement}
      trial={isTrial}
      upgradeTrial={!!entitlement?.upgradeTrial}
      billedElsewhere={isBilledElsewhere}
      isSandbox={isSandboxSupportForSubscriptionsEnabled && !!entitlement?.isSandbox}
      version={isDesignIteration1Enabled ? 2 : undefined}
      isAddOn={!!entitlement?.isAddOn}
      isFutureDatedInactiveEntitlement={isFutureDatedInActiveEntitlement}
      hideInactiveStatus={isFutureDatedInActiveEntitlement}
      isInternalUsageLicenseEntitlement={isBillingProfileUsedForInternalUsageLicense}
      isApp={isApp ?? false}
    />
  );

  const renderHeading = () => {
    return (
      <div className="entitlement-header-ppc" data-testId="entitlement-header-content">
        <div className="entitlement-name-with-icon">
          {renderEntitlementIcon()}
          <div className="entitlement-name">{entitlement?.productName}</div>
        </div>
        <div className="entitlement-number-with-status">
          <div className="entitlement-number">
            {entitlement?.entitlementNumber && <CopyableText text={entitlement?.entitlementNumber} />}
          </div>
          {renderEntitlementStatus()}
          {isExternallyBilled && <BilledExternallyTag />}
        </div>
      </div>
    );
  };

  const getCloudSiteOrOrganizationLink = () => {
    if (entitlement?.cloudSiteHostname) {
      return (
        <>
          <WorldIcon label="Cloud site" />
          <CloudSiteLink
            siteName={entitlement?.cloudSiteHostname}
            txaId={transactionAccountId}
            cloudSiteId={entitlement?.cloudSiteId}
          />
          <Text>{' | '}</Text>
        </>
      );
    }

    if (entitlement?.provisionedResourceDetails?.organisationName) {
      return (
        <>
          <OfficeBuildingIcon label="Organization" />
          <OrgNameLink
            orgName={entitlement?.provisionedResourceDetails?.organisationName}
            orgId={entitlement?.provisionedResourceDetails?.organisationId}
            txaId={transactionAccountId}
          />
          <Text>{' | '}</Text>
        </>
      );
    }

    if (
      !isOrgProduct({
        productIdOrKey: entitlement?.productId,
        offeringName: entitlement?.edition,
        relatesFromEntitlements: entitlement?.relatesFromEntitlements,
        offering: entitlement?.offering,
      })
    ) {
      return (
        <>
          <WorldIcon label="Cloud site" />
          <Text weight="bold">Setup required</Text>
          <Text>{' | '}</Text>
        </>
      );
    }

    return null;
  };

  const renderHeadingV2 = () => {
    return (
      <EntitlementDetailsHeader>
        {renderEntitlementIcon()}
        <Flex testId="entitlement-header-content-v2" direction="column" gap="space.050">
          <Flex gap="space.100">
            <div className="entitlement-name">{entitlement?.productName}</div>
            {renderEntitlementStatus()}
            {isExternallyBilled && <BilledExternallyTag />}
          </Flex>
          <Flex alignItems="center" gap="space.100">
            {getCloudSiteOrOrganizationLink()}
            <span className="entitlement-number">
              <CopyableText text={entitlement?.entitlementNumber} />
            </span>
          </Flex>
        </Flex>
      </EntitlementDetailsHeader>
    );
  };

  const renderActions = (menuButtonAppearance?: Appearance) => {
    return (
      showPageActions &&
      !isBilledElsewhere && (
        <EntitlementHeaderActions
          entitlement={entitlement}
          refetch={refetch}
          invoiceGroupDetails={invoiceGroupDetails}
          menuButtonAppearance={menuButtonAppearance}
          shouldShowAddOnDetails={isAddOnsTabEnabled}
          isRovoOrgProduct={isRovoOrgProduct(entitlement)}
        />
      )
    );
  };

  const renderPage = () => {
    return (
      <>
        <EntitlementSummary
          entitlement={entitlement}
          refetch={refetch}
          parentProductEntitlements={parentProductEntitlements as []}
          invoiceGroupDetails={invoiceGroupDetails}
          shouldShowAddOnDetails={isAddOnsTabEnabled}
          sandboxParentDetails={{ parent: sandboxParent, loading: sandboxParentLoading }}
          isManuallyBilledSupportEnabled={true}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
          isTWCPhase2Enabled={isTWCPhase2Enabled}
          isMultiInstanceAppsEnabled={isMultiInstanceAppsEnabled}
          hideDetails={isDesignIteration1Enabled}
          isAppEditionSupportEnabled={isAppEditionSupportEnabled}
        />

        {isDesignIteration1Enabled && (
          <EntitlementCards
            entitlement={entitlement}
            invoiceGroupDetails={invoiceGroupDetails}
            refetch={refetch}
            isGracePeriodVisibilityEnabled={isGracePeriodVisibilityEnabled}
            isMultiInstanceAppsEnabled={isMultiInstanceAppsEnabled}
          />
        )}

        <ChangeSubscriptionWarningModal
          payload={{ entitlement: entitlement, invoiceGroupDetails: invoiceGroupDetails, refetch: refetch }}
          onClose={closeModalDialog}
          isOpen={isModalOpen}
          showSandboxesDecoupled={isSandboxDecouplingEnabled}
        />

        <EntitlementPageTabs
          entitlementDetails={{ ...entitlement }}
          parentProductEntitlements={parentProductEntitlements as []}
          refetch={refetch}
          invoiceGroupDetails={invoiceGroupDetails}
          shouldShowAddOnDetails={isAddOnsTabEnabled}
          sandboxParentDetails={{ parent: sandboxParent, loading: sandboxParentLoading }}
          openFeedbackWindow={openFeedbackWindow}
        />
      </>
    );
  };

  const renderPageBackground = () => {
    if (isDesignIteration1Enabled) {
      const breadcrumbsItems = [
        {
          title: 'Home',
          href: routePaths.home,
        },
        {
          title: 'Subscriptions',
          href: routePaths.subscriptions,
        },
        {
          title: entitlement?.entitlementNumber || entitlement?.productName || 'Details',
          href: routePaths.entitlement,
        },
      ];
      const appearance = 'default';
      return (
        <>
          <PageBackgroundV2
            heading={renderHeadingV2()}
            actions={renderActions(appearance)}
            className="entitlement-details-v2"
            breadcrumbsProps={{ items: breadcrumbsItems }}
          >
            {renderPage()}
          </PageBackgroundV2>
          <InProductHelpFeedback isOpen={isFeedbackOpen} onCloseFeedbackWindow={onCloseFeedbackWindow} />
        </>
      );
    }

    return (
      <PageBackground
        heading={renderHeading()}
        backButtonColor={N700}
        backButtonSecondaryColor={N0}
        className="entitlement-details-section-v2"
        imageKey={Background.SUBSCRIPTION_DETAILS_BACKGROUND}
        isBackButtonEnabled={!isNewTab}
        lightBackgroundEnabled
        actions={renderActions()}
      >
        {renderPage()}
      </PageBackground>
    );
  };

  const EntitlementDetailsSection = (
    <>
      <EntitlementBanner
        billingStatus={entitlement.billingStatus}
        pauseBilling={entitlement.pauseBilling}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
      {renderPageBackground()}
    </>
  );

  return EntitlementDetailsSection;
};
